import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, Shield, Loader2, ArrowRight } from "lucide-react";
import { useProfile } from "@/hooks/useProfile";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getUserCurrency } from "@/utils/currency";

const Subscription = () => {
  const { profile, refetchProfile } = useProfile();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);

  const isPro = (profile?.plan_key || "free") === "pro";

  useEffect(() => {
    if (searchParams.get("success") === "true") {
      toast({
        title: "Subscription activated",
        description: "Your account is now Pro. Welcome aboard!",
      });
      void refetchProfile();
    }
  }, [searchParams, toast, refetchProfile]);

  const handleSubscribe = async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No authentication session");

      const response = await fetch(`https://ngfwjlgmdcoxewihfmlt.supabase.co/functions/v1/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          planId: "pro",
          billingPeriod: "monthly",
          currency: getUserCurrency().toLowerCase(),
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to create checkout session");
      }

      window.location.href = data.url;
    } catch (error) {
      console.error("Checkout error:", error);
      toast({
        title: "Subscription error",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setIsPortalLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No authentication session");

      const response = await fetch(`https://ngfwjlgmdcoxewihfmlt.supabase.co/functions/v1/create-portal-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to create billing portal session");
      }

      window.location.href = data.url;
    } catch (error) {
      console.error("Billing portal error:", error);
      toast({
        title: "Billing portal error",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsPortalLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 via-white to-blue-50 dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-950">
      <div className="container mx-auto px-4 py-16 max-w-3xl">
        <div className="text-center mb-10">
          <Badge className={`px-3 py-1 ${isPro ? "bg-emerald-600" : "bg-zinc-200 text-zinc-700"}`}>
            {isPro ? "Pro active" : "Free"}
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold mt-4 text-zinc-900 dark:text-white">
            Subscription
          </h1>
          <p className="text-zinc-600 dark:text-zinc-400 mt-2">
            One plan. €29/month. Everything included.
          </p>
        </div>

        {searchParams.get("canceled") === "true" && (
          <Alert className="mb-6" variant="destructive">
            <AlertDescription>Checkout was canceled. You can try again anytime.</AlertDescription>
          </Alert>
        )}

        <Card className="shadow-lg">
          <CardHeader className="text-center space-y-2">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-sky-500 flex items-center justify-center mx-auto">
              <Shield className="h-7 w-7 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-zinc-900 dark:text-white">
              LaunchGuide Pro
            </CardTitle>
            <p className="text-zinc-600 dark:text-zinc-400">
              Full access to playbooks, analysis, research, and AI agents.
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-zinc-900 dark:text-white">
                €29 <span className="text-lg font-medium text-zinc-500 dark:text-zinc-400">/ month</span>
              </div>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-2">
                Cancel anytime. Secure Stripe checkout.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "All playbooks unlocked",
                "Website analysis & research",
                "AI tools and agents",
                "Competitor insights",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 rounded-lg border border-zinc-200 dark:border-zinc-800 px-3 py-2 bg-white dark:bg-zinc-900/60">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  <span className="text-sm text-zinc-700 dark:text-zinc-300">{item}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3">
              <Button
                size="lg"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                onClick={handleSubscribe}
                disabled={isPro || isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Preparing checkout...
                  </span>
                ) : isPro ? (
                  "You are on Pro"
                ) : (
                  <span className="flex items-center gap-2">
                    Subscribe for €29/month
                    <ArrowRight className="h-4 w-4" />
                  </span>
                )}
              </Button>

              {isPro && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleManageBilling}
                  disabled={isPortalLoading}
                >
                  {isPortalLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Opening portal...
                    </span>
                  ) : (
                    "Manage billing"
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Subscription;
